// =====================================================================
// SurgeryGallery (PME Sprint 4 / S52 · multi-upload S53) — aba "Fotos":
// galeria de cirurgia plástica (antes/depois). Agrupa por procedimento →
// fase (pré → pós), com thumbnails (blob+token), ampliar, metadados e excluir.
//
// S53: UPLOAD MÚLTIPLO. Procedimento + Fase + Data são COMPARTILHADOS no lote;
// o ÂNGULO é POR FOTO (numa cirurgia o mesmo procedimento/fase tem várias
// fotos em ângulos diferentes — frente, perfil, oblíquo). Pedido do Dr.
// 🔒 LGPD: serving auth-gated. Multi-tenant.
// =====================================================================
import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Divider
} from "@material-ui/core";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import AddAPhotoIcon from "@material-ui/icons/AddAPhoto";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";

import {
  PHASE_OPTIONS,
  ANGLE_OPTIONS,
  PHASE_LABELS,
  ANGLE_LABELS,
  PHASE_ORDER,
  uploadSurgeryPhoto,
  listSurgeryPhotos,
  getSurgeryPhotoObjectUrl,
  deleteSurgeryPhoto,
  validateSurgeryPhotoFile,
  surgeryErrorMessage,
  SURGERY_ACCEPT
} from "../../services/surgeryService";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  title: { display: "flex", alignItems: "center", gap: theme.spacing(1), fontWeight: 600 },
  group: { marginBottom: theme.spacing(3) },
  groupTitle: { fontWeight: 600, marginBottom: theme.spacing(1) },
  grid: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1.5) },
  card: {
    width: 150,
    padding: theme.spacing(0.5),
    display: "flex",
    flexDirection: "column",
    position: "relative"
  },
  thumbWrap: {
    width: "100%",
    height: 120,
    borderRadius: 4,
    overflow: "hidden",
    background: theme.palette.action.hover,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer"
  },
  thumbImg: { width: "100%", height: "100%", objectFit: "cover" },
  cardMeta: { padding: theme.spacing(0.5, 0.25, 0), display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" },
  cardNotes: { fontSize: 11.5, color: theme.palette.text.secondary, marginTop: 2, whiteSpace: "pre-wrap" },
  delBtn: {
    position: "absolute",
    top: 2,
    right: 2,
    background: "rgba(0,0,0,0.45)",
    color: "#fff",
    padding: 4,
    "&:hover": { background: "rgba(220,38,38,0.85)" }
  },
  empty: { color: theme.palette.text.secondary, fontStyle: "italic", padding: theme.spacing(2, 0) },
  viewerImg: { maxWidth: "100%", maxHeight: "75vh", display: "block", margin: "0 auto" },
  viewerClose: { position: "absolute", top: 8, right: 8, color: "#fff", background: "rgba(0,0,0,0.4)" },
  // S53 multi-upload
  sharedRow: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap" },
  fileList: { marginTop: theme.spacing(1), maxHeight: "42vh", overflowY: "auto" },
  fileRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  fileThumb: {
    width: 52,
    height: 52,
    borderRadius: 4,
    objectFit: "cover",
    flexShrink: 0,
    background: theme.palette.action.hover
  },
  fileName: { fontSize: 11.5, color: theme.palette.text.secondary, maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
}));

const phaseOrd = (ph) => (PHASE_ORDER[ph] != null ? PHASE_ORDER[ph] : 99);

// Thumbnail que busca a imagem como blob (com token) e exibe.
const SurgeryThumb = ({ photoId, onClick }) => {
  const classes = useStyles();
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const urlRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getSurgeryPhotoObjectUrl(photoId)
      .then((u) => {
        if (!active) {
          URL.revokeObjectURL(u);
          return;
        }
        urlRef.current = u;
        setUrl(u);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [photoId]);

  return (
    <div className={classes.thumbWrap} onClick={onClick}>
      {loading ? (
        <CircularProgress size={22} />
      ) : url ? (
        <img src={url} alt="Foto de cirurgia" className={classes.thumbImg} />
      ) : (
        <PhotoLibraryIcon color="disabled" />
      )}
    </div>
  );
};

// Imagem grande no visualizador (busca o blob de novo).
const ViewerImage = ({ photoId }) => {
  const classes = useStyles();
  const [url, setUrl] = useState(null);
  const urlRef = useRef(null);
  useEffect(() => {
    let active = true;
    getSurgeryPhotoObjectUrl(photoId)
      .then((u) => {
        if (!active) {
          URL.revokeObjectURL(u);
          return;
        }
        urlRef.current = u;
        setUrl(u);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [photoId]);
  return url ? (
    <img src={url} alt="Foto de cirurgia" className={classes.viewerImg} />
  ) : (
    <div style={{ textAlign: "center", padding: 40 }}>
      <CircularProgress />
    </div>
  );
};

const todayISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const DEFAULT_ANGLE = (ANGLE_OPTIONS && ANGLE_OPTIONS[0] && ANGLE_OPTIONS[0].value) || "front";

const SurgeryGallery = ({ patientId }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const inputRef = useRef(null);

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  // S53: lote de upload. pendingFiles = [{ file, previewUrl, angle, notes }].
  const [pendingFiles, setPendingFiles] = useState([]);
  const [shared, setShared] = useState({ procedureName: "", phase: "preop", takenAt: todayISO() });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total }
  const [viewer, setViewer] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSurgeryPhotos(patientId);
      setPhotos((data && data.photos) || []);
    } catch (err) {
      toast.error(surgeryErrorMessage(err));
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  // Revoga os previews dos arquivos pendentes ao desmontar.
  useEffect(
    () => () => {
      pendingFiles.forEach((it) => {
        try {
          URL.revokeObjectURL(it.previewUrl);
        } catch {}
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handlePick = () => inputRef.current && inputRef.current.click();

  // S53: aceita MÚLTIPLOS arquivos. Cada um vira um item com seu próprio ângulo.
  const handleFileSelected = (e) => {
    const files = Array.from((e.target && e.target.files) || []);
    if (e.target) e.target.value = ""; // permite re-selecionar os mesmos arquivos
    if (!files.length) return;
    const items = [];
    files.forEach((file) => {
      const verr = validateSurgeryPhotoFile(file);
      if (verr) {
        toast.error(`${file.name}: ${verr}`);
        return;
      }
      items.push({ file, previewUrl: URL.createObjectURL(file), angle: DEFAULT_ANGLE, notes: "" });
    });
    if (!items.length) return;
    setShared({ procedureName: "", phase: "preop", takenAt: todayISO() });
    setPendingFiles(items);
  };

  // Campo COMPARTILHADO (procedimento / fase / data) — captura value antes do updater (S53).
  const setSharedField = (field) => (e) => {
    const value = e.target.value;
    setShared((prev) => ({ ...prev, [field]: value }));
  };

  // Campo POR-FOTO (ângulo / observação).
  const setItemField = (idx, field) => (e) => {
    const value = e.target.value;
    setPendingFiles((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const closePending = () => {
    pendingFiles.forEach((it) => {
      try {
        URL.revokeObjectURL(it.previewUrl);
      } catch {}
    });
    setPendingFiles([]);
    setUploadProgress(null);
  };

  const handleUpload = async () => {
    if (!pendingFiles.length) return;
    setUploading(true);
    let ok = 0;
    try {
      for (let i = 0; i < pendingFiles.length; i++) {
        setUploadProgress({ done: i, total: pendingFiles.length });
        const it = pendingFiles[i];
        await uploadSurgeryPhoto(patientId, it.file, {
          procedureName: shared.procedureName,
          phase: shared.phase,
          angle: it.angle,
          notes: it.notes,
          takenAt: shared.takenAt
        });
        ok += 1;
      }
      toast.success(ok > 1 ? `${ok} fotos adicionadas.` : "Foto adicionada.");
      closePending();
      await load();
    } catch (err) {
      toast.error(surgeryErrorMessage(err));
      if (ok > 0) await load(); // recarrega o que já subiu
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleConfirmDelete = async () => {
    const photo = deleteTarget;
    setDeleteTarget(null);
    if (!photo) return;
    setBusyId(photo.id);
    try {
      await deleteSurgeryPhoto(photo.id);
      toast.success("Foto removida.");
      await load();
    } catch (err) {
      toast.error(surgeryErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  // Agrupa por procedimento e ordena cada grupo por fase (pré -> pós).
  const groupMap = {};
  photos.forEach((p) => {
    const key = p.procedureName || "Sem procedimento definido";
    if (!groupMap[key]) groupMap[key] = [];
    groupMap[key].push(p);
  });
  Object.keys(groupMap).forEach((k) => {
    groupMap[k].sort((a, b) => phaseOrd(a.phase) - phaseOrd(b.phase) || (a.id - b.id));
  });
  const groupNames = Object.keys(groupMap).sort();

  const count = pendingFiles.length;
  const btnLabel = uploading
    ? uploadProgress
      ? `Enviando ${uploadProgress.done + 1}/${uploadProgress.total}...`
      : "Enviando..."
    : count > 1
    ? `Enviar ${count} fotos`
    : "Enviar";

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={SURGERY_ACCEPT}
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />

      <div className={classes.headerRow}>
        <Typography variant="h6" className={classes.title}>
          <PhotoLibraryIcon fontSize="small" /> Fotos de cirurgia ({photos.length})
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddAPhotoIcon />} onClick={handlePick}>
          Adicionar fotos
        </Button>
      </div>

      {loading ? (
        <CircularProgress size={24} />
      ) : photos.length === 0 ? (
        <Typography variant="body2" className={classes.empty}>
          Nenhuma foto ainda. Adicione fotos de pré e pós-operatório para comparar a evolução.
        </Typography>
      ) : (
        groupNames.map((name) => (
          <div key={name} className={classes.group}>
            <Typography variant="subtitle1" className={classes.groupTitle}>
              {name}
            </Typography>
            {/* Timeline pré→pós (S55): fases com foto destacadas + contagem */}
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center", margin: "2px 0 10px" }}>
              {PHASE_OPTIONS.map((opt, i) => {
                const n = groupMap[name].filter((p) => p.phase === opt.value).length;
                const on = n > 0;
                return (
                  <React.Fragment key={opt.value}>
                    {i > 0 ? <span style={{ color: "#999", fontSize: 12 }}>→</span> : null}
                    <Chip
                      size="small"
                      label={n > 0 ? `${opt.label} · ${n}` : opt.label}
                      color={on ? "primary" : "default"}
                      variant={on ? "default" : "outlined"}
                      style={{ opacity: on ? 1 : 0.4 }}
                    />
                  </React.Fragment>
                );
              })}
            </div>
            <div className={classes.grid}>
              {groupMap[name].map((p) => (
                <Paper key={p.id} variant="outlined" className={classes.card}>
                  {busyId === p.id ? (
                    <div className={classes.thumbWrap}>
                      <CircularProgress size={22} />
                    </div>
                  ) : (
                    <SurgeryThumb photoId={p.id} onClick={() => setViewer(p)} />
                  )}
                  <Tooltip title="Excluir">
                    <IconButton size="small" className={classes.delBtn} onClick={() => setDeleteTarget(p)}>
                      <DeleteOutlineIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <div className={classes.cardMeta}>
                    {p.phase ? <Chip size="small" label={PHASE_LABELS[p.phase] || p.phase} /> : null}
                    {p.angle ? (
                      <Chip size="small" variant="outlined" label={ANGLE_LABELS[p.angle] || p.angle} />
                    ) : null}
                  </div>
                  {p.notes ? <div className={classes.cardNotes}>{p.notes}</div> : null}
                </Paper>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Dialog de upload em LOTE (S53) */}
      <Dialog open={count > 0} onClose={() => (!uploading ? closePending() : null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{count > 1 ? `Adicionar ${count} fotos de cirurgia` : "Adicionar foto de cirurgia"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Procedimento, fase e data valem para {count > 1 ? "todas as fotos" : "a foto"}. O <b>ângulo</b> é definido
            em cada foto abaixo.
          </DialogContentText>

          {/* Compartilhado */}
          <TextField
            label="Procedimento"
            value={shared.procedureName}
            onChange={setSharedField("procedureName")}
            placeholder="ex: Rinoplastia, Mamoplastia..."
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            inputProps={{ maxLength: 200 }}
          />
          <div className={classes.sharedRow}>
            <TextField
              select
              label="Fase"
              value={shared.phase}
              onChange={setSharedField("phase")}
              variant="outlined"
              size="small"
              margin="dense"
              style={{ flex: 1, minWidth: 140 }}
            >
              {PHASE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Data das fotos"
              type="date"
              value={shared.takenAt}
              onChange={setSharedField("takenAt")}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              margin="dense"
              style={{ flex: 1, minWidth: 140 }}
            />
          </div>

          <Divider style={{ margin: "10px 0 2px" }} />
          <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: 6 }}>
            Ângulo de cada foto
          </Typography>

          {/* Por foto: miniatura + ângulo + observação */}
          <div className={classes.fileList}>
            {pendingFiles.map((it, idx) => (
              <div key={idx} className={classes.fileRow}>
                <img src={it.previewUrl} alt={`Foto ${idx + 1}`} className={classes.fileThumb} />
                <TextField
                  select
                  label="Ângulo"
                  value={it.angle}
                  onChange={setItemField(idx, "angle")}
                  variant="outlined"
                  size="small"
                  style={{ flexGrow: 1, minWidth: 130 }}
                >
                  {ANGLE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Observação"
                  value={it.notes}
                  onChange={setItemField(idx, "notes")}
                  variant="outlined"
                  size="small"
                  style={{ flexGrow: 1, minWidth: 130 }}
                  inputProps={{ maxLength: 2000 }}
                />
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePending} disabled={uploading}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleUpload}
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <AddAPhotoIcon />}
          >
            {btnLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visualizador ampliado */}
      <Dialog open={!!viewer} onClose={() => setViewer(null)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <div style={{ position: "relative", background: "#000" }}>
          <IconButton size="small" className={classes.viewerClose} onClick={() => setViewer(null)}>
            <CloseIcon />
          </IconButton>
          {viewer ? <ViewerImage photoId={viewer.id} /> : null}
        </div>
        {viewer ? (
          <DialogContent>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {viewer.procedureName || "Sem procedimento definido"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {[PHASE_LABELS[viewer.phase] || viewer.phase, ANGLE_LABELS[viewer.angle] || viewer.angle]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
            {viewer.notes ? <Typography variant="body2" style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{viewer.notes}</Typography> : null}
          </DialogContent>
        ) : null}
      </Dialog>

      {/* Confirmar exclusão */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} fullScreen={isMobile}>
        <DialogTitle>Remover foto</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remover esta foto de cirurgia? A ação fica registrada na trilha de auditoria.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} style={{ color: "#d32f2f" }}>Remover</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SurgeryGallery;
